import * as Sentry from "@sentry/react";

/**
 * Sends participant information and error details to Sentry
 * @param {Object} options - Information to send to Sentry
 * @param {string} options.email - Participant's email address
 * @param {string} options.surveyId - ID of the survey
 * @param {string} options.surveyName - Name of the survey
 * @param {Error|string} options.error - Error object or error message
 * @param {Object} options.additionalData - Any additional data to include
 * @param {string} options.context - Context where the error occurred (e.g., 'form-submission')
 * @param {string} options.functionName - Name of the function where error occurred (e.g., 'handleSubmit')
 */
const sendParticipantToSentry = ({ 
  email, 
  surveyId, 
  surveyName, 
  error, 
  additionalData = {}, 
  context = 'form-submission',
  functionName = null // New parameter for actual function name
}) => {
  try {
    // Use withScope instead of configureScope
    Sentry.withScope(scope => {
      // Clear existing breadcrumbs to reduce noise
      scope.clearBreadcrumbs();
      
      // Set user context
      scope.setUser({
        email: email || 'anonymous@user.com',
        id: additionalData.responderId || 'unknown'
      });

      // Add focused survey context with only essential information
      scope.setContext("survey_info", {
        survey_id: surveyId,
        survey_name: surveyName,
        questions_count: additionalData.questionsCount || 0,
        answers_count: additionalData.answersCount || 0
      });

      // Add minimal form context
      scope.setContext("form_submission", {
        context: context,
        timestamp: new Date().toISOString(),
        status: additionalData.formStatus || 'unknown',
        function_name: functionName // Include actual function name in context
      });
      
      // Add only essential tags
      scope.setTag("form_url", additionalData.formUrl || window.location.pathname);
      scope.setTag("error_type", additionalData.errorType || "form_error");
      scope.setTag("function_name", functionName || "unknown"); // Tag for easier filtering
      
      // Add only 1-2 essential breadcrumbs
      scope.addBreadcrumb({
        category: 'survey',
        message: `${context}: ${surveyName || surveyId}`,
        level: 'info',
        data: {
          answersCount: additionalData.answersCount,
          questionsCount: additionalData.questionsCount
        }
      });

      // Extract the form URL for the title
      const formUrl = additionalData.formUrl || window.location.pathname;
      
      // Get actual function name or use context if not provided
      const actualFunctionName = functionName || context.split('-').join(' ');
      
      // Create a more useful error title with Form URL and function name
      const errorTitle = `Form URL: ${formUrl} || Function: ${actualFunctionName}`;
      
      // Capture the error with the custom title
      if (error instanceof Error) {
        // Clone the error to avoid mutating the original
        const errorWithCustomTitle = new Error(`${errorTitle} || Error: ${error.message}`);
        errorWithCustomTitle.stack = error.stack;
        errorWithCustomTitle.name = error.name;
        Sentry.captureException(errorWithCustomTitle);
      } else if (error) {
        Sentry.captureMessage(`${errorTitle} || Error: ${error}`, 'error');
      } else {
        Sentry.captureMessage(errorTitle, 'error');
      }
    });
  } catch (sentryError) {
    // Fallback logging if Sentry itself fails
    console.error('Failed to send error to Sentry:', sentryError);
  }
};

export default sendParticipantToSentry;